import { Button } from "../button";
import { Input } from "../input";
import { Textarea } from "../textarea";
import CollectionsCombobox from "./CollectionsCombobox";
import { useCanEditRecord, useMyCollections } from "@/api/hooks";
import {
  HGAnyRecord,
  HGAnyRecordData,
  HGIntervals,
  HGMediaType,
  HGRecordData,
  HGTimeRecordData,
} from "@/common/types";
import { createRecord, updateRecord } from "@/api/record";
import {
  deleteRecordMedia,
  invalidateRecordMedia,
  uploadRecordMedia,
} from "@/api/media";
import { useGetMediaLimitsQuery, useGetRecordMediaQuery } from "@/api/api";
import RecordFormIntervals from "./RecordFormIntervals";
import {
  Controller,
  DefaultValues,
  FormProvider,
  useForm,
} from "react-hook-form";
import { MultiUrlInput, normalizeUrl } from "./MultiUrlInput";
import {
  acceptMediaFiles,
  MediaInput,
  PendingMediaFile,
  PendingMediaStatus,
} from "./MediaInput";
import {
  clipboardMediaFiles,
  isEditableTarget,
  isProbablyUrl,
  mediaTypeForFile,
} from "@/lib/paste";
import { Field, FieldLabel } from "../field";
import { useState } from "react";
import { RecordDelete } from "./RecordDelete";
import { useAppDispatch, useAppSelector } from "@/state/redux/hooks";
import { selectMe, setViewingRecord } from "@/state/redux/slices/ui";
import getGlobalStacks from "@/state/globalStacks";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../tabs";
import { MAX_RECORD_SLUG_LENGTH, normalizeSlug } from "@/common/slug";
import UrlNameField from "./UrlNameField";
import RecordCollectionLink from "./RecordCollectionLink";

export interface RecordFormProps {
  record: HGAnyRecord | HGAnyRecordData;
  onSubmit(): void;
  // Media to preseed the form with (e.g. images pasted while creating a record)
  initialPending?: PendingMediaFile[];
}

type HGTimeRecordFormData = Omit<HGTimeRecordData, keyof HGIntervals> & {
  intervals: HGIntervals;
};

type HGRecordFormData = HGTimeRecordFormData | HGRecordData;

export default function RecordForm({
  record,
  onSubmit,
  initialPending,
}: RecordFormProps) {
  const dispatch = useAppDispatch();
  const me = useAppSelector(selectMe);
  // You can only edit your own records, so the owner is always the current
  // user. Falls back to the account id while no nickname is set.
  const ownerRef = me?.nickname?.nickname || me?.id || "";
  // Typed against the wider (time-having) branch of the union so `intervals`
  // can be assigned below -- `useForm` only needs it assignable to
  // `DefaultValues<HGRecordFormData>`, which this is (a superset of keys).
  const defaultValues: DefaultValues<HGTimeRecordFormData> = {
    title: record?.title || "",
    content: record?.content || "",
    collection_id: record.collection_id,
    url: record.url,
    slug: ("slug" in record ? record.slug : "") || "",
  };

  if ("interval" in record && "end_interval" in record && record.interval) {
    defaultValues.intervals = {
      interval: record.interval,
      end_interval: record.end_interval,
      interval_approximate: !!record.interval_approximate,
      interval_uncertain: !!record.interval_uncertain,
      end_interval_approximate: !!record.end_interval_approximate,
      end_interval_uncertain: !!record.end_interval_uncertain,
    };
  } else {
    // Note records have no interval yet; seed with the currently viewed time
    // so enabling the Time switch has a valid value to render.
    const globalStacks = getGlobalStacks();
    const interval = globalStacks.getInterval();
    defaultValues.intervals = {
      interval,
      end_interval: interval,
      interval_approximate: false,
      interval_uncertain: false,
      end_interval_approximate: false,
      end_interval_uncertain: false,
    };
  }
  const form = useForm<HGRecordFormData>({ defaultValues });

  const [withTime, setWithTime] = useState<boolean>(
    "interval" in record ? !!record.interval : false,
  );

  const [pending, setPending] = useState<PendingMediaFile[]>(
    initialPending ?? [],
  );
  const [deletedIds, setDeletedIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  // Remembers the id after a create so a retry (e.g. after a failed
  // upload) updates the record instead of creating a duplicate
  const [createdId, setCreatedId] = useState<string | null>(null);
  // The collection this record heads. Held here rather than read off `record`
  // because linking one takes effect immediately, while the `record` prop is a
  // snapshot from when the form opened and nothing refetches it.
  const [titleCollectionID, setTitleCollectionID] = useState<string | null>(
    ("title_collection_id" in record ? record.title_collection_id : null) ?? null,
  );

  // Deliberately not the browsed space's collections: a record is filed into
  // the author's own, and the API refuses anything else.
  const myCollections = useMyCollections();
  const canEdit = useCanEditRecord(record);

  const recordId = "id" in record ? record.id : (createdId ?? undefined);

  // A record never belongs to the collection it titles, so offering it here
  // could only produce a choice the server would silently undo.
  const collections = titleCollectionID
    ? myCollections.filter((c) => c.id !== titleCollectionID)
    : myCollections;

  const handleTitleCollectionChange = (collectionID: string | null) => {
    setTitleCollectionID(collectionID);
    if (!collectionID) return;
    // Linking made the server drop this membership. Mirroring it keeps the
    // picker above from showing a collection the record is no longer in —
    // and keeps a later save from looking like a request to put it back.
    const current: string[] = form.getValues("collection_id") ?? [];
    if (current.includes(collectionID)) {
      form.setValue(
        "collection_id",
        current.filter((id) => id !== collectionID),
      );
    }
  };

  // Used to validate pasted media against limits/counts
  const { data: mediaLimits } = useGetMediaLimitsQuery();
  const { data: existingMedia } = useGetRecordMediaQuery(recordId ?? "", {
    skip: !recordId,
  });

  // Reachable only if a caller renders the form for a record it does not own;
  // the affordances that open it are already hidden in that case.
  if (!canEdit) return null;

  // Paste inside an open form: images/audio/docs attach as media; a pasted URL
  // (when not typing in a field) appends to the URL list; plain text pastes
  // natively into the focused field.
  const handlePaste = (e: React.ClipboardEvent<HTMLFormElement>) => {
    const cd = e.clipboardData;
    if (!cd) return;

    const files = clipboardMediaFiles(cd);
    if (files.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      setPending((prev) => {
        const next = [...prev];
        const byType = new Map<HGMediaType, File[]>();
        for (const file of files) {
          const mt = mediaTypeForFile(file);
          byType.set(mt, [...(byType.get(mt) ?? []), file]);
        }
        for (const [mt, mtFiles] of byType) {
          const existingOfType = (existingMedia ?? []).filter(
            (m) => m.media_type === mt && !deletedIds.includes(m.id),
          ).length;
          const pendingOfType = next.filter((p) => p.mediaType === mt).length;
          const { accepted } = acceptMediaFiles(
            mtFiles,
            mt,
            mediaLimits?.[mt],
            existingOfType + pendingOfType,
          );
          next.push(...accepted);
        }
        return next;
      });
      return;
    }

    if (isEditableTarget(e.target)) return;

    const text = cd.getData("text/plain");
    if (text && isProbablyUrl(text)) {
      e.preventDefault();
      e.stopPropagation();
      const current = form.getValues("url") ?? [];
      form.setValue("url", [
        ...current,
        { label: "", url: normalizeUrl(text.trim()) },
      ]);
    }
  };

  const setPendingStatus = (
    localId: string,
    status: PendingMediaStatus,
    error?: string,
  ) => {
    setPending((prev) =>
      prev.map((p) => (p.localId === localId ? { ...p, status, error } : p)),
    );
  };

  const onFormSubmit = async (data: HGRecordFormData) => {
    const base: HGRecordData = {
      title: data.title,
      content: data.content,
      collection_id: data.collection_id,
      url: data.url.filter((u) => u.url.trim() !== ""),
    };

    // Omitted rather than sent empty: the API reads a missing slug as "keep
    // the current one", and on create as "derive it from the title".
    const slug = normalizeSlug(data.slug || "", MAX_RECORD_SLUG_LENGTH);
    if (slug) base.slug = slug;

    const recordData: HGAnyRecordData =
      withTime && "intervals" in data
        ? {
            ...base,
            interval: data.intervals.interval,
            end_interval: data.intervals.end_interval,
            interval_approximate: !!data.intervals.interval_approximate,
            interval_uncertain: !!data.intervals.interval_uncertain,
            // Nothing to qualify without a distinct end date.
            end_interval_approximate:
              !!data.intervals.end_interval &&
              !!data.intervals.end_interval_approximate,
            end_interval_uncertain:
              !!data.intervals.end_interval &&
              !!data.intervals.end_interval_uncertain,
          }
        : base;

    setSaving(true);
    try {
      let targetId: string;
      if (recordId) {
        await updateRecord(recordId, recordData);
        targetId = recordId;
      } else {
        targetId = await createRecord(recordData);
        setCreatedId(targetId);
      }

      let failed = false;

      for (const p of pending) {
        if (p.status === "done") continue;
        setPendingStatus(p.localId, "uploading");
        try {
          await uploadRecordMedia(targetId, p.file, p.mediaType);
          setPendingStatus(p.localId, "done");
        } catch (e) {
          setPendingStatus(
            p.localId,
            "error",
            e instanceof Error ? e.message : "Upload failed",
          );
          failed = true;
        }
      }

      const failedDeletes: string[] = [];
      for (const mediaId of deletedIds) {
        try {
          await deleteRecordMedia(targetId, mediaId);
        } catch {
          failedDeletes.push(mediaId);
          failed = true;
        }
      }
      setDeletedIds(failedDeletes);

      if (pending.length > 0 || deletedIds.length > 0) {
        invalidateRecordMedia(targetId);
      }

      // On failure keep the sheet open: per-file errors stay visible
      // and a re-submit retries only what failed
      if (failed) return;

      dispatch(setViewingRecord(null));
      onSubmit();
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit(onFormSubmit)}
        onPaste={handlePaste}
        data-record-form
        className="h-full w-full flex flex-col"
      >
        <Tabs defaultValue="main" className="flex-grow min-h-0 flex flex-col">
          <div className="px-5 pb-3">
            <TabsList className="w-full bg-card border border-border rounded-lg p-[3px] gap-[3px]">
              <TabsTrigger value="main" className="flex-1 rounded-md text-[12.5px] data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:font-semibold data-[state=inactive]:text-muted-foreground data-[state=inactive]:font-medium">Main</TabsTrigger>
              <TabsTrigger value="time" className="flex-1 rounded-md text-[12.5px] data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:font-semibold data-[state=inactive]:text-muted-foreground data-[state=inactive]:font-medium">
                Time
              </TabsTrigger>
              <TabsTrigger value="media" className="flex-1 rounded-md text-[12.5px] data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:font-semibold data-[state=inactive]:text-muted-foreground data-[state=inactive]:font-medium">Media</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="main" className="min-h-0 overflow-y-auto flex flex-col gap-4 px-5 pb-4">
            <Controller
              name="collection_id"
              control={form.control}
              render={({ field }) => (
                <Field>
                  <FieldLabel className="uppercase text-[10.5px] tracking-[.08em] font-bold text-muted-foreground">Collections</FieldLabel>
                  <CollectionsCombobox
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Without collection"
                    allCollections={collections}
                    instant
                  />
                </Field>
              )}
            />

            <Controller
              name="title"
              control={form.control}
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="recordTitle" className="uppercase text-[10.5px] tracking-[.08em] font-bold text-muted-foreground">Title</FieldLabel>
                  <Input
                    {...field}
                    id="recordTitle"
                    type="text"
                    placeholder="Enter title"
                    className="font-serif text-[14px]"
                    style={{ fontFamily: "var(--font-serif)" }}
                  />
                </Field>
              )}
            />

            <Controller
              name="content"
              control={form.control}
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="recordContent" className="uppercase text-[10.5px] tracking-[.08em] font-bold text-muted-foreground">Text</FieldLabel>
                  <Textarea
                    {...field}
                    id="recordContent"
                    placeholder="Enter text"
                    className="resize-none"
                    rows={8}
                  />
                </Field>
              )}
            />

            <RecordCollectionLink
              recordID={recordId}
              titleCollectionID={titleCollectionID}
              onChange={handleTitleCollectionChange}
            />

            <Controller
              name="slug"
              control={form.control}
              render={({ field }) => {
                const normalized = normalizeSlug(
                  field.value || "",
                  MAX_RECORD_SLUG_LENGTH,
                );
                return (
                  <UrlNameField
                    id="recordSlug"
                    label="URL name"
                    value={field.value || ""}
                    onChange={field.onChange}
                    check={{
                      normalized,
                      // Uniqueness is per user across every record, which the
                      // client cannot check without the full list, so a clash
                      // comes back as a 409 on save.
                      state: normalized ? "ok" : "idle",
                      message: null,
                    }}
                    prefix={`${window.location.host}/${ownerRef}/-/`}
                    hint={
                      recordId
                        ? "Changing this keeps the old link working."
                        : "Leave empty to build it from the title."
                    }
                    placeholder="from-the-title"
                  />
                );
              }}
            />
          </TabsContent>

          {/* No overflow-y-auto: the interval picker virtualises its own list
              and has to own the scrolling. */}
          <TabsContent value="time" className="min-h-0 flex-1 flex flex-col gap-3 px-5 pb-4">
            <Controller
              name="intervals"
              control={form.control}
              render={({ field }) => (
                <RecordFormIntervals
                  id="recordIntervals"
                  value={field.value}
                  onChange={field.onChange}
                  enabled={withTime}
                  onEnabledChange={setWithTime}
                />
              )}
            />
          </TabsContent>

          <TabsContent value="media" className="min-h-0 overflow-y-auto flex flex-col gap-4 px-5 pb-4">
            <MultiUrlInput name="url" />
            <MediaInput
              recordId={recordId}
              pending={pending}
              setPending={setPending}
              deletedIds={deletedIds}
              setDeletedIds={setDeletedIds}
            />
          </TabsContent>
        </Tabs>

        <div className="pt-3 px-5 pb-[18px] border-t flex items-center justify-between flex-shrink-0">
          {"id" in record ? (
            <>
              <RecordDelete record={record} />
              <Button type="submit" className="px-6" disabled={saving}>
                {saving ? "Saving…" : "Update"}
              </Button>
            </>
          ) : (
            <>
              <span />
              <Button type="submit" className="px-6" disabled={saving}>
                {saving ? "Saving…" : "Create"}
              </Button>
            </>
          )}
        </div>
      </form>
    </FormProvider>
  );
}
